import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  useTheme,
  CardActionArea,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Campaign as CampaignIcon,
  EmojiEvents,
  TrendingUp,
  CardGiftcard,
  Business,
  Store,
  ConfirmationNumber,
  Groups,
  VpnKey,
  Assessment,
  Settings
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { DashboardHome } from './participant/DashboardHome';
import logoAssociacao from '../logo.png';

interface MenuItem {
  title: string;
  description: string;
  icon: React.ReactElement;
  path: string;
  highlight?: boolean;
  gradient?: string;
}

const Dashboard: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const handleDashboardAccess = async () => {
      if (!isAuthenticated || !user) {
        navigate('/login');
        return;
      }

      setLoading(false);
    };

    handleDashboardAccess();
  }, [isAuthenticated, user, navigate]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Fluxo do participante (usuário final) tem um visual dedicado — ver
  // frontend/src/pages/participant/DashboardHome.tsx. Associação/SUPER_ADMIN
  // continuam no dashboard administrativo abaixo, sem alteração.
  if (user?.role === 'USUARIO_FINAL') {
    return <DashboardHome />;
  }

  // Gradientes customizados para cada seção - Padrão Verde e Azul
  const gradients = {
    primary: `linear-gradient(135deg, #009929 0%, #1565c0 100%)`, // Verde para Azul (padrão do menu)
    secondary: `linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)`, // Azul escuro
    success: `linear-gradient(135deg, #5ccb5f 0%, #009929 100%)`, // Verde claro para verde
    highlight: `linear-gradient(135deg, #009929 0%, #1565c0 100%)`, // Verde para Azul (destaque)
    info: `linear-gradient(135deg, #0288d1 0%, #01579b 100%)`, // Azul médio
  };

  // Menus para Associação (Gestão) - CORRIGIDO: paths relativos para navegação contextual
  const associationMenuItems: MenuItem[] = [
    {
      title: 'Campanhas',
      description: 'Gerenciar campanhas promocionais e configurações',
      icon: <CampaignIcon sx={{ fontSize: 28 }} />,
      path: '/campaigns', // ✅ CORRIGIDO: path relativo
      gradient: gradients.primary
    },
    {
      title: 'Sorteio ao Vivo',
      description: '🎉 Realize sorteios com apresentação visual espetacular',
      icon: <EmojiEvents sx={{ fontSize: 28 }} />,
      path: '/draws/live', // ✅ CORRIGIDO: path relativo
      highlight: true,
      gradient: gradients.highlight
    },
    {
      title: 'Sorteios',
      description: 'Gerenciar e acompanhar todos os sorteios',
      icon: <TrendingUp sx={{ fontSize: 28 }} />,
      path: '/draws', // ✅ CORRIGIDO: path relativo
      gradient: gradients.secondary
    },
    {
      title: 'Prêmios',
      description: 'Cadastrar e gerenciar prêmios incríveis',
      icon: <CardGiftcard sx={{ fontSize: 28 }} />,
      path: '/prizes', // ✅ CORRIGIDO: path relativo
      gradient: gradients.success
    },
    {
      title: 'Patrocinadores',
      description: 'Gerenciar patrocinadores das campanhas promocionais',
      icon: <Business sx={{ fontSize: 28 }} />,
      path: '/sponsors', // ✅ CORRIGIDO: path relativo
      gradient: gradients.info
    },
    {
      title: 'Lojas',
      description: 'Gerenciar lojas participantes e parcerias',
      icon: <Store sx={{ fontSize: 28 }} />,
      path: '/stores', // ✅ CORRIGIDO: path relativo
      gradient: gradients.primary
    },
    {
      title: 'Usuários',
      description: 'Consultar os usuários finais cadastrados',
      icon: <Groups sx={{ fontSize: 28 }} />,
      path: '/users',
      gradient: gradients.info
    },
    {
      title: 'Gestão de Cupons',
      description: 'Visualizar e gerenciar todos os cupons do sistema',
      icon: <ConfirmationNumber sx={{ fontSize: 28 }} />,
      path: '/coupons', // ✅ CORRIGIDO: path relativo
      gradient: gradients.secondary
    },
    {
      title: 'Lotes de Cupons',
      description: 'Gerenciar lotes e distribuição de cupons',
      icon: <Groups sx={{ fontSize: 28 }} />,
      path: '/coupon-batches', // ✅ CORRIGIDO: path relativo
      gradient: gradients.success
    },
    {
      title: 'Redefinir Senha',
      description: '🔑 Redefinir senha de usuários finais por CPF ou email',
      icon: <VpnKey sx={{ fontSize: 28 }} />,
      path: '/reset-user-password', // ✅ CORRIGIDO: path relativo
      gradient: gradients.info
    },
    {
      title: 'Sorteio de Lojas',
      description: '🏪 Sortear prêmios para lojas participantes',
      icon: <Store sx={{ fontSize: 28 }} />,
      path: '/store-draws',
      gradient: gradients.success
    },
    {
      title: 'Relatórios',
      description: '📊 Relatórios detalhados de cupons por loja e usuário',
      icon: <Assessment sx={{ fontSize: 28 }} />,
      path: '/reports',
      gradient: gradients.primary
    }
  ];

  // Menus para SUPER_ADMIN (Administração do Sistema) - MANTIDO: paths absolutos para admin global
  const superAdminMenuItems: MenuItem[] = [
    {
      title: '🏢 Associações',
      description: 'Gerenciar todas as associações do sistema',
      icon: <Business sx={{ fontSize: 28 }} />,
      path: '/admin/associations', // ✅ MANTIDO: admin global
      gradient: gradients.primary
    },
    {
      title: '💳 Planos',
      description: 'Configurar planos de assinatura e preços',
      icon: <CardGiftcard sx={{ fontSize: 28 }} />,
      path: '/admin/plans', // ✅ MANTIDO: admin global
      gradient: gradients.secondary
    },
    {
      title: '💰 Assinaturas',
      description: 'Acompanhar pagamentos e assinaturas ativas',
      icon: <Assessment sx={{ fontSize: 28 }} />,
      path: '/admin/subscriptions', // ✅ MANTIDO: admin global
      gradient: gradients.success
    },
    {
      title: '👥 Usuários',
      description: 'Gerenciar usuários e permissões',
      icon: <Groups sx={{ fontSize: 28 }} />,
      path: '/admin/users', // ✅ MANTIDO: admin global
      gradient: gradients.info
    },
    {
      title: '🌐 Subdomínios',
      description: 'Dashboard de gestão de subdomínios e métricas',
      icon: <TrendingUp sx={{ fontSize: 28 }} />,
      path: '/admin/subdomains', // ✅ NOVO: dashboard de subdomínios
      gradient: gradients.highlight,
      highlight: true
    },
    {
      title: '📊 Dashboard Global',
      description: 'Visão geral de todo o sistema',
      icon: <TrendingUp sx={{ fontSize: 28 }} />,
      path: '/admin/dashboard', // ✅ MANTIDO: admin global
      gradient: gradients.primary
    },
    {
      title: '⚙️ Configurações',
      description: 'Configurações globais do sistema',
      icon: <Settings sx={{ fontSize: 28 }} />,
      path: '/admin/settings', // ✅ MANTIDO: admin global
      gradient: gradients.secondary
    }
  ];

  const menuItems = user?.role === 'SUPER_ADMIN' ? superAdminMenuItems : associationMenuItems;

  // ✅ SIMPLIFICADO: Função de navegação segura
  const handleMenuClick = async (item: MenuItem) => {
    console.log('🧭 Clique no menu:', item.title, '->', item.path);
    
    // Navegação simples - sem slug
    console.log('👤 Navegação simples para:', item.path);
    navigate(item.path);
  };

  return (
    <>
      <AppHeader 
        title={user?.role === 'SUPER_ADMIN' ? 'Administração do Sistema' : 'Sistema de Sorteios'} 
        subtitle={user?.role === 'SUPER_ADMIN' 
          ? 'Painel de controle global para gerenciar associações e planos'
          : user?.role === 'ASSOCIACAO' 
            ? 'Painel administrativo para gestão completa'
            : 'Sua central de participação'
        }
        icon={<DashboardIcon />}
        showBackButton={false}
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header Section */}
        <Box textAlign="center" mb={6}>
          <Box
            component="img"
            src={logoAssociacao}
            alt="Logo Bray Associação"
            sx={{
              height: { xs: 80, md: 120 },
              width: 'auto',
              mb: 3,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              }
            }}
          />
          
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom
            sx={{
              fontWeight: 700,
              background: gradients.highlight,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            {user?.role === 'SUPER_ADMIN' ? 'Administração Global' : 'Bem-vindo!'}
          </Typography>
          
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              maxWidth: 600, 
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            {user?.role === 'SUPER_ADMIN' 
              ? 'Gerencie todas as associações, planos e configurações do sistema'
              : user?.role === 'ASSOCIACAO' 
                ? 'Sua central de controle para campanhas, sorteios e gestão completa'
                : 'Participe de sorteios incríveis e gerencie seus cupons'
            }
          </Typography>
        </Box>

        {/* Menu Grid */}
        <Grid container spacing={3}>
          {menuItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  background: item.gradient,
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[20],
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255,255,255,0.1)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                  },
                  '&:hover::before': {
                    opacity: 1,
                  }
                }}
              >
                <CardActionArea 
                  onClick={() => handleMenuClick(item)} // ✅ CORRIGIDO: usa função de navegação contextual
                  sx={{ height: '100%', p: 3 }}
                >
                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Box display="flex" alignItems="flex-start" mb={2}>
                      <Box sx={{ mr: 2, fontSize: '2rem' }}>
                        {item.icon}
                      </Box>
                      {item.highlight && (
                        <Chip 
                          label="DESTAQUE" 
                          size="small" 
                          sx={{ 
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem'
                          }} 
                        />
                      )}
                    </Box>
                    
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 700,
                        mb: 1,
                        fontSize: '1.1rem'
                      }}
                    >
                      {item.title}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        opacity: 0.9,
                        lineHeight: 1.5,
                        fontSize: '0.875rem'
                      }}
                    >
                      {item.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Footer Info */}
        <Box textAlign="center" mt={6} pt={4} borderTop={1} borderColor="divider">
          <Typography variant="body2" color="text.secondary">
            Sistema de Sorteios - Associação Comercial © 2026
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.role === 'SUPER_ADMIN' 
              ? 'Painel de Administração Global'
              : user?.role === 'ASSOCIACAO' 
                ? 'Painel de Gestão da Associação'
                : 'Portal do Participante'
            }
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default Dashboard;